import React, { useState } from 'react';
import { Routes, Route, NavLink, useNavigate, Navigate } from 'react-router-dom';
import {
  LayoutDashboard, Users, CalendarCheck, CreditCard,
  TrendingUp, Megaphone, LogOut, Menu, X, Flower2, Layers, Sparkles, ClipboardList
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { FinancialProvider } from '../context/FinancialContext';
import AdminHome from './admin/AdminHome';
import AdminStudents from './admin/AdminStudents';
import AdminAttendance from './admin/AdminAttendance';
import AdminPayments from './admin/AdminPayments';
import AdminProgress from './admin/AdminProgress';
import AdminAnnouncements from './admin/AdminAnnouncements';
import AdminBatches from './admin/AdminBatches';
import AdminShowcase from './admin/AdminShowcase';
import AdminProfileDetails from './admin/AdminProfileDetails';
import AdminStudentProfileView from './admin/AdminStudentProfileView';

const NAV_ITEMS = [
  { label: 'Dashboard', icon: LayoutDashboard, path: '/admin' },
  { label: 'Batches', icon: Layers, path: '/admin/batches' },
  { label: 'Students', icon: Users, path: '/admin/students' },
  { label: 'Attendance', icon: CalendarCheck, path: '/admin/attendance' },
  { label: 'Payments', icon: CreditCard, path: '/admin/payments' },
  { label: 'Progress', icon: TrendingUp, path: '/admin/progress' },
  { label: 'Announcements', icon: Megaphone, path: '/admin/announcements' },
  { label: 'Student Profiles', icon: ClipboardList, path: '/admin/profiles' },
  { label: 'Visitor Showcase', icon: Sparkles, path: '/admin/showcase' },
];

function SidebarLink({ item, onClick }) {
  return (
    <NavLink
      to={item.path}
      end={item.path === '/admin'}
      onClick={onClick}
      data-testid={`admin-nav-${item.label.toLowerCase()}`}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group
        ${isActive
          ? 'bg-[#FFEDD5] text-[#D97706] font-medium'
          : 'text-[#57534E] hover:bg-[#FFFAF0] hover:text-[#D97706]'
        }`
      }
    >
      <item.icon className="w-5 h-5 shrink-0" strokeWidth={1.5} />
      <span className="font-body text-sm">{item.label}</span>
    </NavLink>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const Sidebar = ({ mobile = false }) => (
    <aside
      className={`flex flex-col h-full bg-white border-r border-[#E7E5E4]/60
        ${mobile ? 'w-72' : 'w-64'}`}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-6 py-5 border-b border-[#E7E5E4]/60">
        <img
          src="https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/mi9gwt69_image.png"
          alt="icon"
          className="w-10 h-10 rounded-xl shrink-0 object-contain"
        />
        <div>
          <p className="font-heading text-sm font-semibold text-[#292524] leading-tight">Meena's Fitness Forever</p>
          <p className="text-xs text-[#A8A29E] font-body">Admin Panel</p>
        </div>
        {mobile && (
          <button
            className="ml-auto text-[#A8A29E] hover:text-[#292524]"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map(item => (
          <SidebarLink
            key={item.path}
            item={item}
            onClick={() => mobile && setSidebarOpen(false)}
          />
        ))}
      </nav>

      {/* User info + logout */}
      <div className="px-4 py-4 border-t border-[#E7E5E4]/60">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-9 h-9 bg-[#F59E0B] rounded-full flex items-center justify-center text-white font-heading font-semibold text-sm shrink-0">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#292524] font-body truncate">{user?.name || 'Admin'}</p>
            <p className="text-xs text-[#A8A29E] font-body">{user?.phone}</p>
          </div>
        </div>
        <button
          data-testid="admin-logout-button"
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-4 py-2.5 rounded-xl text-[#EF4444] hover:bg-red-50 transition-colors text-sm font-body"
        >
          <LogOut className="w-4 h-4" strokeWidth={1.5} />
          Sign Out
        </button>
      </div>
    </aside>
  );

  return (
    <FinancialProvider>
    <div className="flex h-screen bg-[#FFFAF0] overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden lg:flex">
        <Sidebar />
      </div>

      {/* Mobile drawer overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
          />
          <div className="relative z-50 animate-slide-in-left">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile topbar */}
        <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-[#E7E5E4]/60 shrink-0">
          <button
            data-testid="admin-mobile-menu-button"
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl hover:bg-[#FFFAF0] text-[#57534E]"
          >
            <Menu className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="https://customer-assets.emergentagent.com/job_womens-wellness-72/artifacts/mi9gwt69_image.png"
              alt="icon"
              className="w-7 h-7 rounded-lg shrink-0 object-contain"
            />
            <span className="font-heading font-semibold text-[#292524] text-sm">Meena's Fitness Forever</span>
          </div>
          <div className="w-9 h-9 bg-[#F59E0B] rounded-full flex items-center justify-center text-white font-heading font-semibold text-sm">
            {user?.name?.[0]?.toUpperCase() || 'A'}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Routes>
            <Route index element={<AdminHome />} />
            <Route path="batches" element={<AdminBatches />} />
            <Route path="students" element={<AdminStudents />} />
            <Route path="attendance" element={<AdminAttendance />} />
            <Route path="payments" element={<AdminPayments />} />
            <Route path="progress" element={<AdminProgress />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="showcase" element={<AdminShowcase />} />
            <Route path="profiles" element={<AdminProfileDetails />} />
            <Route path="profiles/:studentId" element={<AdminStudentProfileView />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </main>
      </div>
    </div>
    </FinancialProvider>
  );
}
